import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, cursosService, configuracionService } from '../services/api';
import PlantillaCertificadoEditor from '../components/PlantillaCertificadoEditor';
import ConfirmPopup from '../components/ConfirmPopup';
import './Certificados.css';

const TAB_PENDIENTES = 'pendientes';
const TAB_GENERADOS = 'generados';

const hoyISO = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const Certificados = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(TAB_PENDIENTES);
  const [cursos, setCursos] = useState([]);
  const [cursosFinalizados, setCursosFinalizados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandidoId, setExpandidoId] = useState(null);
  const [estudiantesPorCurso, setEstudiantesPorCurso] = useState({});
  const [loadingEstudiantes, setLoadingEstudiantes] = useState({});
  const [actualizandoCertificado, setActualizandoCertificado] = useState(null);
  const [descargando, setDescargando] = useState(null);
  /** Estado desde API: curso_id -> true si todos los aprobados tienen certificado (evita parpadeo Pendientes/Generados) */
  const [certificadosCompletosPorCurso, setCertificadosCompletosPorCurso] = useState({});
  /** Fecha de emisión por curso (una para todos los certificados del curso) */
  const [fechaCertificadoPorCurso, setFechaCertificadoPorCurso] = useState({});
  const [actualizandoFechaCurso, setActualizandoFechaCurso] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaPorCurso, setPlantillaPorCurso] = useState({});
  const [actualizandoPlantillaCurso, setActualizandoPlantillaCurso] = useState(null);
  const [mostrarEditorPlantillas, setMostrarEditorPlantillas] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadCursos();
    loadPlantillas();
  }, [navigate]);

  const loadPlantillas = async () => {
    try {
      const data = await configuracionService.getPlantillasCertificado();
      setPlantillas(Array.isArray(data) ? data.filter((p) => p.activo !== false) : []);
    } catch {
      setPlantillas([]);
    }
  };

  // Cargar estudiantes de cursos finalizados cuando se cambia a Generados para poder desmarcar
  useEffect(() => {
    if (tab === TAB_GENERADOS && cursosFinalizados.length > 0) {
      const cargarEstudiantesGenerados = async () => {
        for (const curso of cursosFinalizados) {
          if (!estudiantesPorCurso[curso.id]) {
            try {
              const data = await cursosService.getResumenEstudiantes(curso.id);
              const lista = Array.isArray(data) ? data : (data?.estudiantes || []);
              const aprobados = lista.filter((e) => e.aprobado === true);
              setEstudiantesPorCurso((prev) => ({ ...prev, [curso.id]: aprobados }));
              if (data?.fecha_certificado_curso != null) {
                setFechaCertificadoPorCurso((prev) => ({ ...prev, [curso.id]: data.fecha_certificado_curso }));
              }
              if (data?.plantilla_certificado_id !== undefined) {
                setPlantillaPorCurso((prev) => ({ ...prev, [curso.id]: data.plantilla_certificado_id }));
              }
            } catch (err) {
              setEstudiantesPorCurso((prev) => ({ ...prev, [curso.id]: [] }));
            }
          }
        }
      };
      cargarEstudiantesGenerados();
    }
  }, [tab, cursosFinalizados]);


  const loadCursos = async () => {
    try {
      setLoading(true);
      setError('');
      const [data, estado] = await Promise.all([
        cursosService.getCursos(),
        cursosService.getCertificadosEstado().catch(() => ({})),
      ]);
      const list = Array.isArray(data) ? data : [];
      setCursos(list);
      const hoy = hoyISO();
      const finalizados = list.filter((c) => {
        const fin = c.fecha_fin;
        if (!fin) return false;
        const finStr = typeof fin === 'string' ? fin.substring(0, 10) : (fin && typeof fin.substring === 'function' ? fin.substring(0, 10) : '');
        return finStr && finStr < hoy;
      });
      setCursosFinalizados(finalizados);
      setCertificadosCompletosPorCurso(typeof estado === 'object' && estado !== null ? estado : {});
      const plantillasMap = {};
      finalizados.forEach((c) => {
        if (c.plantilla_certificado != null) {
          plantillasMap[c.id] = c.plantilla_certificado;
        }
      });
      setPlantillaPorCurso((prev) => ({ ...prev, ...plantillasMap }));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar cursos');
      setCursosFinalizados([]);
      setCertificadosCompletosPorCurso({});
    } finally {
      setLoading(false);
    }
  };

  const toggleExpandir = async (cursoId) => {
    if (expandidoId === cursoId) {
      setExpandidoId(null);
      return;
    }
    setExpandidoId(cursoId);
    if (estudiantesPorCurso[cursoId]) return;
    try {
      setLoadingEstudiantes((prev) => ({ ...prev, [cursoId]: true }));
      const data = await cursosService.getResumenEstudiantes(cursoId);
      const lista = Array.isArray(data) ? data : (data?.estudiantes || []);
      const aprobados = lista.filter((e) => e.aprobado === true);
      setEstudiantesPorCurso((prev) => ({ ...prev, [cursoId]: aprobados }));
      if (data?.fecha_certificado_curso != null) {
        setFechaCertificadoPorCurso((prev) => ({ ...prev, [cursoId]: data.fecha_certificado_curso }));
      }
      if (data?.plantilla_certificado_id !== undefined) {
        setPlantillaPorCurso((prev) => ({ ...prev, [cursoId]: data.plantilla_certificado_id }));
      }
    } catch (err) {
      setEstudiantesPorCurso((prev) => ({ ...prev, [cursoId]: [] }));
    } finally {
      setLoadingEstudiantes((prev) => ({ ...prev, [cursoId]: false }));
    }
  };

  const cambiarPlantillaCurso = async (cursoId, plantillaId) => {
    if (actualizandoPlantillaCurso === cursoId) return;
    const valor = plantillaId === '' || plantillaId == null ? null : Number(plantillaId);
    try {
      setActualizandoPlantillaCurso(cursoId);
      const data = await cursosService.updatePlantillaCertificadoCurso(cursoId, valor);
      setPlantillaPorCurso((prev) => ({ ...prev, [cursoId]: data.plantilla_certificado_id ?? null }));
      setCursos((prev) => prev.map((c) => (
        c.id === cursoId ? { ...c, plantilla_certificado: data.plantilla_certificado_id ?? null } : c
      )));
      setCursosFinalizados((prev) => prev.map((c) => (
        c.id === cursoId ? { ...c, plantilla_certificado: data.plantilla_certificado_id ?? null } : c
      )));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar plantilla');
    } finally {
      setActualizandoPlantillaCurso(null);
    }
  };

  const renderControlesCurso = (curso, todosCert) => (
    <div className="certificados-seleccionar-todos">
      <label className="certificados-checkbox-label">
        <input
          type="checkbox"
          checked={todosCert}
          onChange={() => seleccionarTodosCurso(curso.id)}
          disabled={actualizandoCertificado?.startsWith(`${curso.id}-`)}
          aria-label="Seleccionar todos"
        />
        <span>Seleccionar todos del curso</span>
      </label>
      <span className="certificados-fecha-curso-wrap">
        <label className="certificados-fecha-curso-label">Fecha de emisión:</label>
        <input
          type="date"
          className="certificados-input-fecha"
          value={fechaCertificadoPorCurso[curso.id] ? String(fechaCertificadoPorCurso[curso.id]).substring(0, 10) : ''}
          onChange={(ev) => cambiarFechaCertificadoCurso(curso.id, ev.target.value || null)}
          disabled={actualizandoFechaCurso === curso.id}
          title="Misma fecha para todos los certificados de este curso"
        />
      </span>
      <span className="certificados-plantilla-wrap">
        <label className="certificados-fecha-curso-label" htmlFor={`plantilla-curso-${curso.id}`}>Plantilla:</label>
        <select
          id={`plantilla-curso-${curso.id}`}
          className="certificados-select-plantilla"
          value={plantillaPorCurso[curso.id] ?? curso.plantilla_certificado ?? ''}
          onChange={(ev) => cambiarPlantillaCurso(curso.id, ev.target.value)}
          disabled={actualizandoPlantillaCurso === curso.id}
        >
          <option value="">Diseño por defecto</option>
          {plantillas.map((p) => (
            <option key={p.id} value={p.id}>{p.nombre}</option>
          ))}
        </select>
      </span>
    </div>
  );

  const toggleCertificado = async (cursoId, userId, nuevoValor) => {
    const key = `${cursoId}-${userId}`;
    if (actualizandoCertificado === key) return;
    try {
      setActualizandoCertificado(key);
      const data = await cursosService.updateEstudianteCertificado(cursoId, userId, nuevoValor);
      setEstudiantesPorCurso((prev) => {
        const lista = prev[cursoId] || [];
        const nextLista = lista.map((e) => (e.id === userId ? { ...e, certificado: data.certificado } : e));
        const todosCert = nextLista.length > 0 && nextLista.every((e) => e.certificado === true);
        setCertificadosCompletosPorCurso((p) => ({ ...p, [cursoId]: todosCert }));
        return { ...prev, [cursoId]: nextLista };
      });
    } catch (err) {
      console.error('Error al actualizar certificado:', err);
      setError(err.response?.data?.error || 'Error al actualizar certificado');
    } finally {
      setActualizandoCertificado(null);
    }
  };

  const cambiarFechaCertificadoCurso = async (cursoId, nuevaFecha) => {
    if (actualizandoFechaCurso === cursoId) return;
    try {
      setActualizandoFechaCurso(cursoId);
      const data = await cursosService.updateFechaCertificadoCurso(cursoId, nuevaFecha || null);
      setFechaCertificadoPorCurso((prev) => ({ ...prev, [cursoId]: data.fecha_certificado ?? null }));
    } catch (err) {
      console.error('Error al actualizar fecha del curso:', err);
      setError(err.response?.data?.error || 'Error al actualizar fecha');
    } finally {
      setActualizandoFechaCurso(null);
    }
  };

  const seleccionarTodosCurso = async (cursoId) => {
    const lista = estudiantesPorCurso[cursoId] || [];
    if (lista.length === 0) return;
    const todosCertificados = lista.every((e) => e.certificado === true);
    const nuevoValor = !todosCertificados;
    for (const estudiante of lista) {
      await toggleCertificado(cursoId, estudiante.id, nuevoValor);
    }
  };

  const descargarCertificado = async (cursoId, userId) => {
    const key = `${cursoId}-${userId}`;
    if (descargando === key) return;
    try {
      setDescargando(key);
      const blob = await cursosService.getCertificadoPdfForUser(cursoId, userId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error(err);
    } finally {
      setDescargando(null);
    }
  };

  const todosCertificadosEnCurso = (cursoId) => {
    const lista = estudiantesPorCurso[cursoId] || [];
    if (lista.length === 0) return false;
    return lista.every((e) => e.certificado === true);
  };

  const cursosPendientes = cursosFinalizados.filter((c) => !certificadosCompletosPorCurso[c.id]);
  const cursosGenerados = cursosFinalizados.filter((c) => certificadosCompletosPorCurso[c.id]);

  return (
    <div className="certificados-page">
      <div className="certificados-header">
        <div className="certificados-header-top">
          <div>
            <h1>Certificados</h1>
            <p>Gestión y generación de certificados para el campus.</p>
          </div>
          <button
            type="button"
            className="certificados-btn-plantillas"
            onClick={() => setMostrarEditorPlantillas(true)}
          >
            Agregar / modificar plantilla
          </button>
        </div>
      </div>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      <div className="certificados-tabs">
        <button
          type="button"
          className={`certificados-tab ${tab === TAB_PENDIENTES ? 'certificados-tab-active' : ''}`}
          onClick={() => setTab(TAB_PENDIENTES)}
        >
          Pendientes
        </button>
        <button
          type="button"
          className={`certificados-tab ${tab === TAB_GENERADOS ? 'certificados-tab-active' : ''}`}
          onClick={() => setTab(TAB_GENERADOS)}
        >
          Generados
        </button>
      </div>

      {tab === TAB_PENDIENTES && (
        <div className="certificados-pendientes">
          {loading ? (
            <div className="certificados-loading">
              <div className="certificados-spinner" />
              <p>Cargando cursos finalizados...</p>
            </div>
          ) : cursosPendientes.length === 0 ? (
            <p className="certificados-empty">No hay cursos pendientes. Cuando todos los aprobados de un curso estén tildados, el curso pasará a Generados.</p>
          ) : (
            <div className="certificados-curso-list">
              {cursosPendientes.map((curso) => {
                const expandido = expandidoId === curso.id;
                const estudiantes = estudiantesPorCurso[curso.id] || [];
                const loadingEst = loadingEstudiantes[curso.id];
                const todosCert = todosCertificadosEnCurso(curso.id);

                return (
                  <div key={curso.id} className="certificados-curso-block">
                    <button
                      type="button"
                      className="certificados-curso-row"
                      onClick={() => toggleExpandir(curso.id)}
                      aria-expanded={expandido}
                    >
                      <span className="certificados-curso-nombre">{curso.nombre}</span>
                      {curso.codigo && <span className="certificados-curso-codigo">{curso.codigo}</span>}
                      <span className="certificados-curso-fecha">
                        {curso.fecha_fin ? new Date(curso.fecha_fin).toLocaleDateString('es-AR') : '—'}
                      </span>
                      <span className="certificados-curso-flecha">{expandido ? '▼' : '▶'}</span>
                    </button>

                    {expandido && (
                      <div className="certificados-curso-detalle">
                        {loadingEst ? (
                          <div className="certificados-loading-inline">
                            <div className="certificados-spinner" />
                            <p>Cargando inscriptos aprobados...</p>
                          </div>
                        ) : estudiantes.length === 0 ? (
                          <p className="certificados-sin-aprobados">No hay inscriptos aprobados en este curso.</p>
                        ) : (
                          <>
                            {renderControlesCurso(curso, todosCert)}
                            <div className="certificados-table-scroll">
                              <table className="certificados-table">
                                <thead>
                                  <tr>
                                    <th className="certificados-col-check">Sel.</th>
                                    <th className="certificados-col-alumno">Alumno</th>
                                    <th className="certificados-col-email">Email</th>
                                    <th className="certificados-col-cert">Certificado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {estudiantes.map((e) => {
                                    const key = `${curso.id}-${e.id}`;
                                    const estaActualizando = actualizandoCertificado === key;
                                    return (
                                      <tr key={e.id}>
                                        <td className="certificados-col-check">
                                          <input
                                            type="checkbox"
                                            checked={!!e.certificado}
                                            onChange={() => toggleCertificado(curso.id, e.id, !e.certificado)}
                                            disabled={estaActualizando}
                                            aria-label={`Marcar certificado para ${e.nombre}`}
                                          />
                                        </td>
                                        <td className="certificados-col-alumno">{e.nombre || e.email || '—'}</td>
                                        <td className="certificados-col-email">{e.email || '—'}</td>
                                        <td className="certificados-col-cert">
                                          {e.certificado && (
                                            <button
                                              type="button"
                                              className="certificados-btn-certificado"
                                              onClick={() => descargarCertificado(curso.id, e.id)}
                                              disabled={descargando === key}
                                              title="Ver o descargar certificado"
                                            >
                                              🏆 Certificado
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === TAB_GENERADOS && (
        <div className="certificados-generados">
          {loading ? (
            <div className="certificados-loading">
              <div className="certificados-spinner" />
              <p>Cargando cursos generados...</p>
            </div>
          ) : cursosGenerados.length === 0 ? (
            <p className="certificados-placeholder">No hay cursos en Generados. Tildá todos los inscriptos aprobados de un curso en Pendientes para que pase aquí.</p>
          ) : (
            <div className="certificados-curso-list">
              {cursosGenerados.map((curso) => {
                const expandido = expandidoId === curso.id;
                const estudiantes = estudiantesPorCurso[curso.id] || [];
                const loadingEst = loadingEstudiantes[curso.id];
                const todosCert = todosCertificadosEnCurso(curso.id);

                return (
                  <div key={curso.id} className="certificados-curso-block">
                    <button
                      type="button"
                      className="certificados-curso-row"
                      onClick={() => toggleExpandir(curso.id)}
                      aria-expanded={expandido}
                    >
                      <span className="certificados-curso-nombre">{curso.nombre}</span>
                      {curso.codigo && <span className="certificados-curso-codigo">{curso.codigo}</span>}
                      <span className="certificados-curso-fecha">
                        {curso.fecha_fin ? (() => {
                          const s = String(curso.fecha_fin).slice(0, 10);
                          const [y, m, d] = s.split('-').map(Number);
                          if ([y, m, d].some(Number.isNaN)) return '—';
                          return new Date(y, m - 1, d).toLocaleDateString('es-AR');
                        })() : '—'}
                      </span>
                      <span className="certificados-badge-generado">✓ Generado</span>
                      <span className="certificados-curso-flecha">{expandido ? '▼' : '▶'}</span>
                    </button>

                    {expandido && (
                      <div className="certificados-curso-detalle">
                        {loadingEst ? (
                          <div className="certificados-loading-inline">
                            <div className="certificados-spinner" />
                            <p>Cargando inscriptos aprobados...</p>
                          </div>
                        ) : estudiantes.length === 0 ? (
                          <p className="certificados-sin-aprobados">No hay inscriptos aprobados en este curso.</p>
                        ) : (
                          <>
                            {renderControlesCurso(curso, todosCert)}
                            <div className="certificados-table-scroll">
                              <table className="certificados-table">
                                <thead>
                                  <tr>
                                    <th className="certificados-col-check">Cert.</th>
                                    <th className="certificados-col-alumno">Alumno</th>
                                    <th className="certificados-col-email">Email</th>
                                    <th className="certificados-col-cert">Certificado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {estudiantes.map((e) => {
                                    const key = `${curso.id}-${e.id}`;
                                    const estaActualizando = actualizandoCertificado === key;
                                    return (
                                      <tr key={e.id}>
                                        <td className="certificados-col-check">
                                          <input
                                            type="checkbox"
                                            checked={!!e.certificado}
                                            onChange={() => toggleCertificado(curso.id, e.id, !e.certificado)}
                                            disabled={estaActualizando}
                                            aria-label={`Marcar certificado para ${e.nombre}`}
                                          />
                                        </td>
                                        <td className="certificados-col-alumno">{e.nombre || e.email || '—'}</td>
                                        <td className="certificados-col-email">{e.email || '—'}</td>
                                        <td className="certificados-col-cert">
                                          {e.certificado && (
                                            <button
                                              type="button"
                                              className="certificados-btn-certificado"
                                              onClick={() => descargarCertificado(curso.id, e.id)}
                                              disabled={descargando === key}
                                              title="Ver o descargar certificado"
                                            >
                                              🏆 Certificado
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <PlantillaCertificadoEditor
        open={mostrarEditorPlantillas}
        onClose={() => setMostrarEditorPlantillas(false)}
        onSaved={loadPlantillas}
      />
    </div>
  );
};

export default Certificados;
